import { useMemo, useState } from 'react';
import { useAdvisor } from '@/lib/advisor-context';
import type { ProjectTab } from '@/lib/advisor-context';
import { buildCompareMatrixFromProjects } from '@/lib/compare-table';
import { emiBasisLakhs, projectFromBookedVisit } from '@/lib/live-project';
import {
  CONCIERGE_SERVICES,
  VISIT_DATES,
  driveMinutes,
  emiCalc,
  enrichProject,
  fmtClock,
  fmtInr,
  fmtL,
} from '@/lib/prototype-catalog';
import { DummyBadge } from './dummy-badge';
import { buildLiveVisitCalendar, buildVisitMonth, timeFromProposedLabel, VISIT_TIME_CHIPS } from '@/lib/visit-board';
import type { MarketIntel } from '@/lib/advisor-api';

/**
 * Area value intel for the price tab — approved, provenance-carrying rows
 * only. Absent data renders the honest gap, never the dummy psf/cagr overlay
 * (those stay quarantined in the illustrative catalog).
 */
function ValueIntel({ intel, location }: { intel?: MarketIntel; location: string }) {
  if (!intel) {
    return (
      <div className="na-coming-soon">
        <div className="na-meta-label">Area value intel</div>
        <p>
          No verified value data for {location || 'this area'} yet — we only show numbers
          that carry a source.
        </p>
      </div>
    );
  }
  const isFloorValues = intel.provenance.source.startsWith('tier_a:igr_guidance');
  const basis = isFloorValues ? 'Official floor values' : 'Price trend';
  const trend =
    intel.appreciation.five_yr_pct != null
      ? { pct: intel.appreciation.five_yr_pct, years: 5 }
      : intel.appreciation.three_yr_pct != null
        ? { pct: intel.appreciation.three_yr_pct, years: 3 }
        : null;
  return (
    <div className="na-coming-soon">
      <div className="na-meta-label">Area value intel · {intel.display_name}</div>
      {trend ? (
        <p>
          {basis} {trend.pct >= 0 ? '+' : ''}
          {Math.round(trend.pct)}% over {trend.years} yrs
        </p>
      ) : (
        <p>Trend % pending — baseline records are still being verified.</p>
      )}
      {intel.drivers.length > 0 && (
        <div className="na-timeline">
          {intel.drivers.map((d) => (
            <div key={d.event} className="na-timeline-row">
              <div className="na-timeline-date">{d.date ?? ''}</div>
              <div>
                <div className="na-timeline-title">{d.event}</div>
                {d.note && <div className="na-timeline-detail">{d.note}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="na-board-hint na-board-hint-tight">
        Source: {intel.provenance.source} · as of {intel.provenance.as_of}
        {isFloorValues ? '. Regulator floor values are not market prices.' : ''}
      </p>
    </div>
  );
}

function JourneySteps() {
  const { briefPhase, board, showAllMatches, setBoardView } = useAdvisor();
  const step =
    briefPhase === 'collecting'
      ? 1
      : board.view === 'project'
        ? 3
        : board.view === 'visit'
          ? 4
          : board.view === 'compare'
            ? 2
            : 2;

  const goStep = (n: number) => {
    if (n === 2) showAllMatches();
    else if (n === 3 && board.projectId) setBoardView('project');
    else if (n === 4) setBoardView('visit');
  };

  const steps = ['Brief', 'Matches', 'Explore', 'Visit'] as const;
  return (
    <div className="na-journey" aria-label="Your progress">
      {steps.map((label, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        const clickable = n >= 2 && !(n === 3 && !board.projectId);
        return (
          <button
            key={label}
            type="button"
            disabled={!clickable}
            className={`na-journey-step${active ? ' na-journey-active' : ''}${done ? ' na-journey-done' : ''}${clickable ? ' na-journey-click' : ''}`}
            onClick={() => clickable && goStep(n)}
          >
            <span className="na-journey-num">{n}</span>
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function BoardColumn() {
  const { board, matchProjects } = useAdvisor();
  const hasCatalog = matchProjects.length > 0;
  const kicker =
    !hasCatalog
      ? 'Getting started'
      : board.view === 'project'
        ? 'In focus'
        : board.view === 'visit'
          ? 'Visit day'
          : board.view === 'compare'
            ? 'Compare'
          : board.view === 'concierge'
            ? 'Concierge'
            : 'Your matches';

  return (
    <aside className="na-board-col">
      <div className="na-board-head">
        <div className="na-board-head-row">
          <span className="na-board-kicker">{kicker}</span>
          <span className="na-board-label">Your board</span>
        </div>
        {hasCatalog && <JourneySteps />}
      </div>
      <div className="na-scroll na-board-scroll">
        {!hasCatalog && board.view === 'welcome' && <BoardWelcome />}
        {hasCatalog && board.view === 'matches' && <BoardFocusHub />}
        {hasCatalog && board.view === 'compare' && <BoardCompare />}
        {hasCatalog && board.view === 'project' && <BoardProject />}
        {board.view === 'visit' && <BoardVisit />}
        {board.view === 'concierge' && <BoardConcierge />}
      </div>
    </aside>
  );
}

export function BoardFocusHub() {
  const {
    matchProjects,
    board,
    openBoardProject,
    switchBoardFocus,
    exploreMoreProjects,
    compareAllMatches,
    shortlist,
    addToShortlist,
    removeFromShortlist,
    engagedProjects,
    planVisitDay,
    focusedProject,
  } = useAdvisor();

  if (matchProjects.length === 0) return null;

  const exploredCount = engagedProjects.length;

  return (
    <div className="na-board-panel na-board-focus-hub">
      <p className="na-board-hint na-board-hint-tight">
        {focusedProject
          ? `In focus: ${focusedProject.name} — ask anything in chat, or tap another match to switch.`
          : matchProjects.length === 1
            ? 'One match so far — tap to focus, or ask Naya for more options.'
            : `${matchProjects.length} matches — tap one to focus, ask questions in chat; explored projects fill your visit day.`}
      </p>

      <div className="na-all-matches">
        {matchProjects.map((p) => {
          const focused = board.projectId === p.id;
          const explored = shortlist.some((s) => s.id === p.id);
          return (
            <div key={p.id} className={`na-match-row${focused ? ' na-match-row-focus' : ''}`}>
              <button type="button" className="na-match-row-main" onClick={() => switchBoardFocus(p.id)}>
                <div>
                  <div className="na-match-row-title">
                    {p.name}
                    {focused && <span className="na-focus-badge-inline">In focus</span>}
                    {!focused && explored && (
                      <span className="na-focus-badge-inline na-explored-badge">Explored</span>
                    )}
                  </div>
                  <div className="na-match-sub">
                    {p.builder} · {p.location}
                  </div>
                  {p.fit && (
                    <div className={`na-fit na-fit-${p.fitTone ?? 'neutral'} na-match-fit`}>{p.fit}</div>
                  )}
                </div>
                <div className="na-match-price">{p.price}</div>
              </button>
              <div className="na-match-row-actions">
                <button type="button" className="na-link-btn" onClick={() => openBoardProject(p.id)}>
                  Open
                </button>
                <button
                  type="button"
                  className="na-link-btn"
                  onClick={() => (explored ? removeFromShortlist(p.id) : addToShortlist(p))}
                >
                  {explored ? 'Explored' : 'Save'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {exploredCount > 0 && (
        <button type="button" className="na-btn-dark na-btn-block na-explore-more" onClick={() => planVisitDay()}>
          {exploredCount === 1
            ? 'Plan visit day (1 explored)'
            : `Plan visit day (${exploredCount} explored)`}
        </button>
      )}

      {matchProjects.length >= 2 && (
        <button type="button" className="na-btn-outline na-btn-block" onClick={() => compareAllMatches()}>
          Compare all {matchProjects.length}
        </button>
      )}

      <button type="button" className="na-btn-outline na-btn-block na-explore-more" onClick={() => exploreMoreProjects()}>
        Show me more projects
      </button>

      <div className="na-roadmap-block">
        <div className="na-board-kicker">Earned so far</div>
        <EarnedRoadmap />
      </div>
    </div>
  );
}

function BoardWelcome() {
  return (
    <div className="na-board-panel">
      <h2 className="na-board-title">Everything, before you call.</h2>
      <p className="na-board-lead">
        Nothing is earned yet — keep talking. Each lane below lights up when the chat
        earns it, and only then. Weigh a home fully without speaking to anyone.
      </p>
      <EarnedRoadmap />
    </div>
  );
}

/** The earned lanes: six lanes that light only when this session actually
 *  produced that evidence. Lit state is derived from real context state —
 *  in live mode only server-loaded packs count — never from a script.
 *  Shared by the roadmap (two-pane) and the One-Thread board deck/badge. */
export interface EarnedLane {
  n: string;
  title: string;
  desc: string;
  on: boolean;
  note: string;
}

export function useEarnedLanes(): EarnedLane[] {
  const {
    matchProjects,
    engagedProjects,
    getBoardProject,
    mode,
    emiTouched,
    visitRouteStopIds,
    bookedVisits,
    visitItinerary,
  } = useAdvisor();

  return useMemo(() => {
    const details = engagedProjects.map((p) => getBoardProject(p.id));
    // Demo boards are labeled illustrative throughout, so sample packs may
    // light lanes there; live lanes require a real NayaDesk payload.
    const counted = mode === 'live' ? details.filter((p) => p.live === true) : details;
    const unitCount = counted.reduce((n, p) => n + (p.configurations?.length ?? 0), 0);
    const legalHit = counted.find((p) => (p.statuses?.length ?? 0) > 0 || p.rera);
    const priceHit = counted.find((p) => p.price || p.marketIntel);
    const stops = visitItinerary?.stops?.length ?? visitRouteStopIds.length;
    const visitOn = stops > 0 || bookedVisits.length > 0;
    return [
      {
        n: '01',
        title: 'Matches for your brief',
        desc: 'Projects that fit budget + area',
        on: matchProjects.length > 0,
        note: `${matchProjects.length} on your board`,
      },
      {
        n: '02',
        title: 'Units & floor plans',
        desc: 'Real configurations, honest sizes',
        on: unitCount > 0,
        note: `${unitCount} configuration${unitCount === 1 ? '' : 's'} read`,
      },
      {
        n: '03',
        title: 'Legal & title pack',
        desc: 'RERA, khata, EC — before you call',
        on: !!legalHit,
        note: legalHit ? `${legalHit.name} checked` : '',
      },
      {
        n: '04',
        title: 'Price & cost rows',
        desc: 'What it costs, with caveats',
        on: !!priceHit,
        note: 'Earned in this chat',
      },
      {
        n: '05',
        title: 'Home-loan & EMI fit',
        desc: 'EMI against your down payment',
        on: emiTouched,
        note: 'You ran the numbers',
      },
      {
        n: '06',
        title: 'Visit route',
        desc: 'One day, routed — reveal only then',
        on: visitOn,
        note: stops > 0 ? `${stops} stop${stops === 1 ? '' : 's'}` : 'Booked',
      },
    ];
  }, [
    matchProjects,
    engagedProjects,
    getBoardProject,
    mode,
    emiTouched,
    visitRouteStopIds,
    bookedVisits,
    visitItinerary,
  ]);
}

function EarnedRoadmap() {
  const lanes = useEarnedLanes();
  return (
    <div className="na-roadmap" aria-label="What this chat has earned">
      {lanes.map((l) => (
        <div key={l.n} className={l.on ? 'na-road-item na-road-item-on' : 'na-road-item'}>
          <span className="na-road-num">{l.on ? '✓' : l.n}</span>
          <span className="na-road-body">
            <span className="na-road-title">{l.title}</span>
            <span className="na-road-sub">{l.on && l.note ? l.note : l.desc}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

function BoardMatches() {
  const { matchProjects, openBoardProject, shortlist, addToShortlist, removeFromShortlist } =
    useAdvisor();
  const projects = matchProjects;

  return (
    <div className="na-board-panel na-board-matches">
      {matchProjects.length === 0 && (
        <p className="na-board-hint">
          Tell Naya your brief in chat — matches appear here when the bot shortlists projects.
        </p>
      )}
      {projects.map((p) => {
        const saved = shortlist.some((s) => s.id === p.id);
        return (
          <div key={p.id} className="na-match-card">
            <div className="na-match-head">
              <div>
                <div className="na-match-title-row">
                  <h3>{p.name}</h3>
                  {p.tag && <span className="na-tag">{p.tag}</span>}
                  {p.dummy && <DummyBadge />}
                </div>
                <div className="na-match-sub">
                  {p.builder} · {p.location}
                </div>
              </div>
              <div className="na-match-price-col">
                <div className="na-match-price">{p.price}</div>
                <div className="na-match-psf">₹{p.psf.toLocaleString('en-IN')}/sqft</div>
              </div>
            </div>
            <div className="na-match-meta">
              <div>
                <div className="na-meta-label">Config</div>
                <div>{p.bhk}</div>
              </div>
              <div>
                <div className="na-meta-label">Carpet</div>
                <div>{p.sqft}</div>
              </div>
              <div>
                <div className="na-meta-label">Possession</div>
                <div>{p.possession}</div>
              </div>
            </div>
            <p className={`na-fit na-fit-${p.fitTone ?? 'neutral'}`}>{p.fit}</p>
            <div className="na-match-actions">
              <button type="button" className="na-link-btn" onClick={() => openBoardProject(p.id)}>
                Open & run tools
              </button>
              <button
                type="button"
                className="na-save-btn"
                onClick={() => (saved ? removeFromShortlist(p.id) : addToShortlist(p))}
              >
                {saved ? '✓ Shortlisted' : '+ Shortlist'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function BoardProject() {
  const {
    board,
    shortlist,
    setProjectTab,
    setBoardView,
    emi,
    setEmiField,
    addToShortlist,
    removeFromShortlist,
    sendText,
    getBoardProject,
    projectDetailLoading,
  } = useAdvisor();

  const project = useMemo(() => getBoardProject(board.projectId), [getBoardProject, board.projectId]);
  const loading = projectDetailLoading === board.projectId;
  const totalL = emiBasisLakhs(project);
  const calc = emiCalc(totalL, emi.down, emi.rate, emi.tenure);
  const saved = shortlist.some((s) => s.id === project.id);
  const configs = project.configurations ?? [];
  const hasLive = !!project.live;

  const tabs = [
    ['legal', 'Legal & title'],
    ['units', 'Unit types'],
    ['price', 'Price'],
    ['emi', 'EMI fit'],
  ] as const satisfies ReadonlyArray<[ProjectTab, string]>;

  return (
    <div className="na-board-project">
      <div className="na-project-top">
        <button type="button" className="na-back-link" onClick={() => setBoardView('matches')}>
          ← all matches
        </button>
        <div className="na-project-header">
          <div>
            <h2>{project.name}</h2>
            <div className="na-match-sub">
              {project.builder} · {project.location}
            </div>
            {loading && <span className="na-live-tag">Loading live data…</span>}
            {!loading && hasLive && <span className="na-live-tag">Live from NayaDesk</span>}
            {!loading && !hasLive && project.dummy !== false && (
              <DummyBadge label="illustrative until opened" />
            )}
          </div>
          <div className="na-project-price-col">
            <div className="na-match-price">{project.price || '—'}</div>
            <button
              type="button"
              className="na-save-btn"
              onClick={() => (saved ? removeFromShortlist(project.id) : addToShortlist(project))}
            >
              {saved ? '✓ Shortlisted' : '+ Shortlist'}
            </button>
          </div>
        </div>
        <div className="na-tabs">
          {tabs.map(([k, label]) => (
            <button
              key={k}
              type="button"
              className={board.tab === k ? 'na-tab na-tab-active' : 'na-tab'}
              onClick={() => setProjectTab(k)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {board.tab === 'units' && (
        <div className="na-board-panel">
          {configs.length > 0 ? (
            <div className="na-config-list">
              {configs.map((c) => (
                <div key={c.unitType} className="na-config-row">
                  <div>
                    <div className="na-meta-label">Configuration</div>
                    <div>{c.unitType}</div>
                    {c.sizeDisplay ? <div className="na-match-sub">{c.sizeDisplay}</div> : null}
                  </div>
                  <div className="na-config-price">{c.priceDisplay || 'Price on request'}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="na-board-hint">
              {loading
                ? 'Pulling unit types from catalog…'
                : 'Unit types will appear here once loaded — ask in chat: “What configurations are available?”'}
            </p>
          )}
          {project.projectType && (
            <p className="na-board-hint na-board-hint-tight">Project type · {project.projectType.replace(/_/g, ' ')}</p>
          )}
          <div className="na-coming-soon">
            <div className="na-meta-label">Floor plate picker</div>
            <p>Interactive floor grid with sold/available units — coming soon.</p>
          </div>
        </div>
      )}

      {board.tab === 'legal' && (
        <div className="na-board-panel">
          {project.statuses.length === 0 && loading && (
            <p className="na-board-hint">Loading legal snapshot…</p>
          )}
          {project.statuses.map((st) => (
            <div key={st.label} className="na-legal-row">
              <CheckIcon />
              <div>
                <div className="na-meta-label">{st.label}</div>
                <div>{st.value}</div>
              </div>
            </div>
          ))}
          {project.rera && !project.statuses.some((s) => s.label === 'RERA') && (
            <div className="na-rera">RERA · {project.rera}</div>
          )}
          {project.summary && <p className="na-insight">{project.summary}</p>}
          {!hasLive && project.timeline.length > 0 && (
            <div className="na-timeline">
              {project.timeline.map((tl) => (
                <div key={tl.title} className="na-timeline-row">
                  <div className="na-timeline-date">{tl.date}</div>
                  <div>
                    <div className="na-timeline-title">{tl.title}</div>
                    <div className="na-timeline-detail">{tl.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {hasLive && (
            <p className="na-board-hint na-board-hint-tight">
              Ask in chat for anything else — e.g. “Is the title clear?” or “Any litigation history?”
            </p>
          )}
        </div>
      )}

      {board.tab === 'price' && (
        <div className="na-board-panel">
          {hasLive ? (
            <>
              <div className="na-price-live">
                <Stat label="Starting from" value={project.price || '—'} sub={project.possession ? `Possession ${project.possession}` : undefined} />
              </div>
              {configs.length > 0 && (
                <div className="na-config-list">
                  {configs.map((c) => (
                    <div key={c.unitType} className="na-config-row">
                      <span>{c.unitType}</span>
                      <span>{c.priceDisplay}</span>
                    </div>
                  ))}
                </div>
              )}
              <ValueIntel {...(project.marketIntel ? { intel: project.marketIntel } : {})} location={project.location} />
            </>
          ) : (
            <>
              <div className="na-coming-soon">
                <p>Open this project or ask about pricing in chat to load live quotes from NayaDesk.</p>
              </div>
            </>
          )}
        </div>
      )}

      {board.tab === 'emi' && (
        <div className="na-board-panel">
          <div className="na-emi-head">
            <span className="na-emi-num">{fmtInr(calc.emi)}</span>
            <span>/ month</span>
          </div>
          <div className="na-board-hint">
            on {project.name} · {fmtL(totalL)} {hasLive ? '(live starting price)' : '(indicative)'}
          </div>
          <Slider label="Down payment" value={emi.down} min={5} max={50} display={`${emi.down}%`} onChange={(v) => setEmiField('down', v)} />
          <Slider label="Interest rate" value={emi.rate} min={7} max={10} step={0.1} display={`${emi.rate.toFixed(1)}%`} onChange={(v) => setEmiField('rate', v)} />
          <Slider label="Tenure" value={emi.tenure} min={5} max={30} display={`${emi.tenure} yrs`} onChange={(v) => setEmiField('tenure', v)} />
          <div className="na-emi-summary">
            <Row k="Down payment" v={fmtInr(calc.downAmt)} />
            <Row k="Loan principal" v={fmtInr(calc.P)} />
            <Row k="Total interest" v={fmtInr(calc.interest)} />
          </div>
          {project.banks && <p className="na-board-hint">Loan eligibility · {project.banks}</p>}
        </div>
      )}

      <div className="na-project-foot">
        <button type="button" className="na-btn-dark na-btn-block" onClick={() => setBoardView('visit')}>
          Plan a visit
        </button>
        <button type="button" className="na-btn-outline" onClick={() => sendText('Compare all three')}>
        Compare on board
      </button>
        <button type="button" className="na-link-btn na-back-link" onClick={() => setBoardView('matches')}>
          ← All matches
        </button>
      </div>
    </div>
  );
}

export function BoardCompare() {
  const {
    compareMatrix,
    matchProjects,
    setBoardView,
    openBoardProject,
    compareAllMatches,
    getBoardProject,
  } = useAdvisor();

  const matrix = useMemo(() => {
    if (compareMatrix?.projects.length && compareMatrix.rows.length) return compareMatrix;
    return buildCompareMatrixFromProjects(matchProjects.slice(0, 3).map((p) => getBoardProject(p.id)));
  }, [compareMatrix, matchProjects, getBoardProject]);

  if (!matrix || matrix.projects.length < 2) {
    return (
      <div className="na-board-panel">
        <button type="button" className="na-back-link" onClick={() => setBoardView('matches')}>
          ← all matches
        </button>
        <p className="na-board-hint">
          Need at least two projects to compare. Ask in chat for more matches, or open projects from your list.
        </p>
        {matchProjects.length >= 2 && (
          <button type="button" className="na-btn-dark na-btn-block" onClick={() => compareAllMatches()}>
            Compare my matches
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="na-board-panel na-board-compare">
      <button type="button" className="na-back-link" onClick={() => setBoardView('matches')}>
        ← all matches
      </button>
      <h2 className="na-board-title-sm">Side by side</h2>
      <p className="na-board-lead na-board-hint-tight">
        Live catalog data — ask in chat if you want to go deeper on any row.
      </p>

      <div className="na-compare-scroll">
        <table className="na-compare-table">
          <thead>
            <tr>
              <th scope="col" />
              {matrix.projects.map((p) => (
                <th key={p.project_id} scope="col">
                  <button
                    type="button"
                    className="na-compare-project"
                    onClick={() => openBoardProject(p.project_id)}
                  >
                    {p.name}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.rows.map((row) => (
              <tr key={row.label}>
                <th scope="row">{row.label}</th>
                {row.values.map((val, i) => (
                  <td key={`${row.label}-${matrix.projects[i]?.project_id ?? i}`}>{val || '—'}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="na-compare-foot">
        {matrix.projects.map((p) => (
          <button
            key={p.project_id}
            type="button"
            className="na-btn-outline"
            onClick={() => openBoardProject(p.project_id)}
          >
            Open {p.name.split(' ')[0]}
          </button>
        ))}
      </div>
    </div>
  );
}

export function BoardVisit() {
  const {
    visitRouteStopIds,
    visitQueue,
    visitItinerary,
    shortlist,
    matchProjects,
    bookedVisits,
    visitDate,
    visitSkipped,
    setVisitDate,
    toggleVisitStop,
    revealed,
    confirmReveal,
    setBoardView,
    sendText,
    mode,
  } = useAdvisor();
  /** Inline consent form — stay on home workspace; never navigate to /reveal
   *  (that remounted chat via the catch-all route and misfired turns). */
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealName, setRevealName] = useState('');
  const [revealPhone, setRevealPhone] = useState('');
  const [revealError, setRevealError] = useState('');
  const [revealBusy, setRevealBusy] = useState(false);

  const calendarDates = useMemo(
    () => (mode === 'live' ? buildLiveVisitCalendar(14) : VISIT_DATES),
    [mode],
  );
  const visitMonth = useMemo(() => (mode === 'live' ? buildVisitMonth() : null), [mode]);
  const [visitTime, setVisitTime] = useState<string | null>(null);

  const stopIds = visitRouteStopIds;
  const proposedLabel = visitQueue?.proposed_label?.trim() || null;
  const activeStopId = visitQueue?.active?.project_id ?? null;
  const proposedTime =
    visitQueue?.awaiting_confirm && proposedLabel
      ? timeFromProposedLabel(proposedLabel)
      : null;
  const dateObj = calendarDates.find((d) => d.id === visitDate);
  const slotLabel =
    visitItinerary?.day_label ??
    proposedLabel ??
    dateObj?.label ??
    bookedVisits[0]?.label ??
    null;

  const routeFromItinerary = visitItinerary?.stops
    .map((stop, i) => {
      const raw =
        shortlist.find((s) => s.id === stop.project_id) ??
        matchProjects.find((p) => p.id === stop.project_id);
      const booked = bookedVisits.find((v) => v.projectId === stop.project_id);
      const p = raw
        ? enrichProject(raw)
        : booked
          ? enrichProject(projectFromBookedVisit(booked, matchProjects))
          : null;
      if (!p) return null;
      const driveLabel =
        stop.drive_from_prev_min != null ? `${stop.drive_from_prev_min} min drive` : null;
      const time =
        stop.label?.match(/(\d{1,2}(?::\d{2})?\s*[AP]M)/i)?.[1] ??
        (stop.status === 'queued' ? 'TBD' : '—');
      return {
        time,
        name: p.name,
        area: p.location,
        driveLabel,
        booked: stop.status === 'booked',
      };
    })
    .filter((r): r is NonNullable<typeof r> => r != null) ?? [];

  let clk = 9 * 60 + 30;
  const routeLegacy = stopIds.flatMap((id, i) => {
    const booked = bookedVisits.find((v) => v.projectId === id);
    const raw = shortlist.find((s) => s.id === id) ?? matchProjects.find((p) => p.id === id);
    const p = raw ? enrichProject(raw) : booked ? enrichProject(projectFromBookedVisit(booked, matchProjects)) : null;
    if (!p) return [];
    let driveLabel: string | null = null;
    if (i > 0) {
      const dm = driveMinutes(stopIds[i - 1]!, id);
      clk += dm;
      driveLabel = `${dm} min drive`;
    }
    const bookedTime = booked?.label.match(/(\d{1,2}(?::\d{2})?\s*[AP]M)/i)?.[1];
    const serverTime =
      mode === 'live' && !booked && id === activeStopId && proposedTime ? proposedTime : null;
    const arrive = bookedTime ?? serverTime ?? fmtClock(clk);
    if (bookedTime) {
      const m = /(\d{1,2})(?::(\d{2}))?\s*([AP]M)/i.exec(bookedTime);
      if (m) {
        let h = parseInt(m[1]!, 10) % 12;
        if (/pm/i.test(m[3]!)) h += 12;
        const min = m[2] ? parseInt(m[2], 10) : 0;
        clk = h * 60 + min + 45;
      }
    } else if (!serverTime) {
      clk += 45;
    }
    return [{ time: arrive, name: p.name, area: p.location, driveLabel, booked: !!booked }];
  });

  const route = routeFromItinerary.length > 0 ? routeFromItinerary : routeLegacy;

  return (
    <div className="na-board-panel">
      <button type="button" className="na-back-link" onClick={() => setBoardView('matches')}>
        ← all matches
      </button>
      <h2 className="na-board-title-sm">Your visit day</h2>
      <p className="na-board-lead">
        Chat sets the slot — this board mirrors what you agreed on. Review the route, then confirm
        when it looks right.
      </p>

      {bookedVisits.length > 0 && (
        <Section title="Confirmed">
          {bookedVisits.map((v) => (
            <div key={v.projectId} className="na-confirmed-box na-visit-confirmed">
              <CheckIcon />
              <div>
                <p>
                  <b>{v.projectName}</b> · {v.label}
                </p>
                <p className="na-board-hint na-board-hint-tight">
                  Saved with our team — they&apos;ll confirm details before the day.
                </p>
              </div>
            </div>
          ))}
        </Section>
      )}

      <Section title="01 · Your stops">
        {stopIds.length === 0 ? (
          <p className="na-board-hint">
            Explore projects in chat or on the board — each one you ask about appears here for visit
            day.
          </p>
        ) : (
          stopIds.map((id) => {
            const booked = bookedVisits.find((v) => v.projectId === id);
            const p =
              shortlist.find((s) => s.id === id) ??
              matchProjects.find((m) => m.id === id) ??
              (booked ? projectFromBookedVisit(booked, matchProjects) : null);
            if (!p) return null;
            const inc = !visitSkipped[id];
            return (
              <div key={id} className="na-visit-stop">
                <div>
                  <div className="na-visit-stop-name">{p.name}</div>
                  <div className="na-match-sub">
                    {p.location}
                    {booked ? ` · ${booked.label}` : ''}
                  </div>
                </div>
                {booked ? (
                  <span className="na-live-tag">Booked</span>
                ) : id === activeStopId ? (
                  <span className="na-live-tag">Visiting</span>
                ) : (
                  <button type="button" className={inc ? 'na-toggle on' : 'na-toggle'} onClick={() => toggleVisitStop(id)}>
                    {inc ? 'Visiting' : 'Skipped'}
                  </button>
                )}
              </div>
            );
          })
        )}
      </Section>

      <Section title="02 · Your visit time">
        {proposedLabel ? (
          <div className="na-confirmed-box na-visit-confirmed na-proposed-slot">
            <p>
              <b>{proposedLabel}</b>
            </p>
            <p className="na-board-hint na-board-hint-tight">From your chat — confirm when the route looks right.</p>
          </div>
        ) : mode === 'live' && visitMonth ? (
          <>
            <p className="na-board-hint">Pick a day, then a time — today is open if hours still are.</p>
            <div className="na-cal-title">{visitMonth.title}</div>
            <div className="na-cal-grid">
              {visitMonth.headers.map((h) => (
                <div key={h} className="na-cal-dow">
                  {h}
                </div>
              ))}
              {visitMonth.cells.map((cell) =>
                cell.selectable && cell.id ? (
                  <button
                    key={cell.key}
                    type="button"
                    className={[
                      'na-cal-day',
                      'sel',
                      visitDate === cell.id ? 'on' : '',
                      cell.isToday ? 'today' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => {
                      setVisitDate(cell.id);
                      setVisitTime(null);
                    }}
                  >
                    {cell.day}
                  </button>
                ) : (
                  <div key={cell.key} className={`na-cal-day${cell.isToday ? ' today' : ''}`}>
                    {cell.day ?? ''}
                  </div>
                ),
              )}
            </div>
            {visitDate ? (
              <>
                <p className="na-board-hint na-board-hint-tight">Time</p>
                <div className="na-floor-row">
                  {VISIT_TIME_CHIPS.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className={visitTime === t.id ? 'na-floor-chip na-floor-active' : 'na-floor-chip'}
                      onClick={() => {
                        setVisitTime(t.id);
                        const cell = visitMonth.cells.find((c) => c.id === visitDate);
                        const when = cell?.chatText || cell?.label;
                        if (when) sendText(`${when} at ${t.label}`);
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </>
            ) : null}
          </>
        ) : (
          <>
            <p className="na-board-hint">Tell Naya a day and time in chat (e.g. Monday 11AM).</p>
            <p className="na-board-hint na-board-hint-tight">Quick pick — Saturdays only:</p>
            <div className="na-floor-row">
              {calendarDates.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className={visitDate === d.id && !proposedLabel ? 'na-floor-chip na-floor-active' : 'na-floor-chip'}
                  onClick={() => {
                    setVisitDate(d.id);
                    sendText('chatText' in d && d.chatText ? d.chatText : d.label);
                  }}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </>
        )}
      </Section>

      <Section title="03 · Your route">
        {(slotLabel && stopIds.length > 0) || bookedVisits.length > 0 ? (
          <>
            <div className="na-route-map">
              route map · {slotLabel ?? 'your visit'} ·{' '}
              {bookedVisits.length || proposedLabel ? <span className="na-live-tag">Live</span> : <DummyBadge />}
            </div>
            {route.map((r) => (
              <div key={r.name}>
                {r.driveLabel && <div className="na-drive">↓ {r.driveLabel}</div>}
                <div className="na-route-row">
                  <span className="na-route-dot" />
                  <span className="na-route-time">{r.time}</span>
                  <span>
                    <strong>{r.name}</strong> · {r.area}
                  </span>
                </div>
              </div>
            ))}
            <p className="na-board-hint">Wraps up around {fmtClock(clk)}.</p>
          </>
        ) : (
          <p className="na-board-hint">Choose a date to see the optimised order and drive times.</p>
        )}
      </Section>

      <Section title="04 · Confirm privately">
        {!revealed ? (
          <div className="na-reveal-box">
            <p>
              Your number reaches only the <b>{stopIds.length}</b> builders on this route, only on{' '}
              {slotLabel ?? 'your visit day'}.
            </p>
            {!revealOpen ? (
              <button
                type="button"
                className="na-btn-dark na-btn-block"
                disabled={stopIds.length === 0 || (!slotLabel && !dateObj)}
                onClick={() => {
                  setRevealError('');
                  setRevealOpen(true);
                }}
              >
                Reveal my number & confirm
              </button>
            ) : (
              <div className="na-reveal-form">
                <p className="na-board-hint na-board-hint-tight">
                  Share contact for{' '}
                  <b>
                    {shortlist.find((s) => s.id === stopIds[0])?.name ??
                      matchProjects.find((p) => p.id === stopIds[0])?.name ??
                      'this visit'}
                  </b>{' '}
                  only — no chat turn, no route change.
                </p>
                <label className="na-board-hint" htmlFor="board-reveal-name">
                  Your name
                </label>
                <input
                  id="board-reveal-name"
                  className="na-reveal-input"
                  autoComplete="name"
                  placeholder="e.g. Priya"
                  value={revealName}
                  onChange={(e) => setRevealName(e.target.value)}
                />
                <label className="na-board-hint" htmlFor="board-reveal-phone">
                  WhatsApp number
                </label>
                <input
                  id="board-reveal-phone"
                  className="na-reveal-input"
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder="+91 98XXX XXXXX"
                  value={revealPhone}
                  onChange={(e) => setRevealPhone(e.target.value)}
                />
                {revealError ? <p className="na-reveal-error">{revealError}</p> : null}
                <button
                  type="button"
                  className="na-btn-dark na-btn-block"
                  disabled={revealBusy}
                  onClick={() => {
                    void (async () => {
                      const project =
                        shortlist.find((s) => s.id === stopIds[0]) ??
                        matchProjects.find((p) => p.id === stopIds[0]);
                      if (!project) {
                        setRevealError('Pick a stop on the route first.');
                        return;
                      }
                      if (revealName.trim().length < 2) {
                        setRevealError('Add your name so the site team knows who to expect.');
                        return;
                      }
                      if (revealPhone.replace(/\D/g, '').length < 10) {
                        setRevealError('Enter a valid 10-digit WhatsApp number.');
                        return;
                      }
                      setRevealError('');
                      setRevealBusy(true);
                      const result = await confirmReveal({
                        project,
                        buyerName: revealName.trim(),
                        buyerPhone: revealPhone.trim(),
                        visitLabel: slotLabel ?? undefined,
                      });
                      setRevealBusy(false);
                      if (!result.ok) {
                        setRevealError(result.error ?? 'Could not share contact — try again.');
                        return;
                      }
                      setRevealOpen(false);
                    })();
                  }}
                >
                  {revealBusy ? 'Sharing…' : 'Confirm — this visit only'}
                </button>
                <button
                  type="button"
                  className="na-btn-outline na-btn-block"
                  onClick={() => setRevealOpen(false)}
                >
                  Not yet
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="na-confirmed-box">
            <CheckIcon />
            <p>
              <b>Confirmed.</b> Itinerary sent to your phone. Your number reached only that
              day&apos;s builders.
            </p>
          </div>
        )}
      </Section>
    </div>
  );
}

function BoardConcierge() {
  const { setBoardView, sendText } = useAdvisor();
  return (
    <div className="na-board-panel">
      <button type="button" className="na-back-link" onClick={() => setBoardView('matches')}>
        ← all matches
      </button>
      <h2 className="na-board-title-sm">Help, only if you want it.</h2>
      <div className="na-concierge-note">
        Advisor is free and anonymous. Every paid service shares only what it needs.
      </div>
      {CONCIERGE_SERVICES.map((c) => (
        <div key={c.id} className={`na-concierge-card${c.tone === 'featured' ? ' featured' : ''}`}>
          <div className="na-concierge-head">
            <h3>{c.title}</h3>
            <span>{c.price}</span>
          </div>
          <p>{c.desc}</p>
          <ul>
            {c.points.map((pt) => (
              <li key={pt}>{pt}</li>
            ))}
          </ul>
          <button
            type="button"
            className={c.tone === 'base' ? 'na-btn-muted' : 'na-btn-dark'}
            onClick={() => {
              if (c.id === 'visit') setBoardView('visit');
              else sendText(`Tell me about ${c.title}`);
            }}
          >
            {c.cta}
          </button>
        </div>
      ))}
    </div>
  );
}

function Spec({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="na-meta-label">{label}</div>
      <div>{value}</div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="na-row">
      <span>{k}</span>
      <span>{v}</span>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <div className="na-meta-label">{label}</div>
      <div className="na-stat-big">{value}</div>
      {sub && <div className="na-board-hint">{sub}</div>}
    </div>
  );
}

function Slider({
  label,
  value,
  min,
  max,
  step = 1,
  display,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  display: string;
  onChange: (v: number) => void;
}) {
  return (
    <div className="na-slider">
      <div className="na-row">
        <span>{label}</span>
        <span>{display}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="na-section">
      <div className="na-board-kicker">{title}</div>
      {children}
    </div>
  );
}

function CheckIcon() {
  return (
    <span className="na-check">
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
        <path d="M20 6 9 17l-5-5" />
      </svg>
    </span>
  );
}
